import { post } from '../api';
import { signal } from '../signal';
import { toast } from '../ui';

export interface Passage {
	moment: string;
	role: string;
	text: string;
	area: string;
	kind: string;
	beat?: number;
	screen?: { title: string; text: string; asset?: string };
	outcome?: string;
	observed?: string;
}

/** A passage in story play: what the player typed, the scene, a refusal, a blocked move, progress, or a system note. */
export interface StoryPassage {
	kind: 'you' | 'scene' | 'beat' | 'refused' | 'blocked' | 'progress' | 'system';
	text: string;
	at: number;
}

/** One live connection to a campaign; exposes its state as signals. */
export class GameSocket {
	readonly connected = signal(false);
	readonly you = signal<any>(null);
	readonly game = signal<any>(null);
	readonly players = signal<any[]>([]);
	readonly view = signal<any>(null);
	readonly feed = signal<Passage[]>([]);
	readonly observed = signal<Passage[]>([]);
	readonly gm = signal<any>(null);
	readonly gmPassages = signal<Passage[]>([]);
	readonly proposals = signal<any[]>([]);
	readonly screen = signal<any>(null);
	readonly ending = signal<any>(null);
	readonly suggestion = signal<any>(null);
	readonly kicked = signal<string | null>(null);
	/** Story play: this player's character view, their passages, and the composer's overview. */
	readonly storyView = signal<any>(null);
	readonly storyFeed = signal<StoryPassage[]>([]);
	readonly storyGm = signal<any>(null);
	readonly skew = signal(0);
	private ws: WebSocket | null = null;
	private closed = false;
	private retry = 0;
	private pending = new Map<string, Array<(d: any) => void>>();

	constructor(readonly code: string) {
		void this.open();
	}

	private async open() {
		if (this.closed) return;
		try {
			const { ticket } = await post(`/api/games/${this.code}/ticket`);
			const proto = location.protocol === 'https:' ? 'wss' : 'ws';
			const ws = new WebSocket(`${proto}://${location.host}/ws/${this.code}?ticket=${encodeURIComponent(ticket)}`);
			this.ws = ws;
			ws.onopen = () => {
				this.connected.set(true);
				this.retry = 0;
				this.send({ t: 'ping' });
			};
			ws.onmessage = (ev) => this.onMessage(JSON.parse(ev.data));
			ws.onclose = (ev) => {
				this.connected.set(false);
				if (ev.code === 4003 || ev.code === 4000) return;
				if (!this.closed) setTimeout(() => this.open(), Math.min(15000, 500 * 2 ** this.retry++));
			};
		} catch (e) {
			if (!this.closed) setTimeout(() => this.open(), Math.min(15000, 1000 * 2 ** this.retry++));
		}
	}

	close() {
		this.closed = true;
		this.ws?.close();
	}

	send(m: unknown) {
		if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
		else toast('Reconnecting…', 'warn');
	}

	/** GM operation; resolves with the server's gm_result. */
	gmOp<T = any>(op: string, data: Record<string, unknown> = {}): Promise<T> {
		return new Promise((resolve) => {
			const q = this.pending.get(op) ?? [];
			q.push(resolve);
			this.pending.set(op, q);
			this.send({ t: 'gm', op, ...data });
		});
	}

	private onMessage(m: any) {
		switch (m.t) {
			case 'story_view':
				this.storyView.set(m.view);
				break;
			case 'story_feed':
				this.storyFeed.set(m.passages ?? []);
				break;
			case 'story_passages':
				this.storyFeed.update((f) => [...f, ...m.passages].slice(-400));
				break;
			case 'story_gm':
				this.storyGm.set(m.gm);
				break;
			case 'welcome':
				this.you.set(m.you);
				this.game.set(m.game);
				this.players.set(m.players);
				break;
			case 'pong':
				this.skew.set(Date.now() - m.now);
				break;
			case 'you':
				this.you.set(m.you);
				break;
			case 'players':
				this.players.set(m.players);
				if (m.game) this.game.set(m.game);
				break;
			case 'view':
				this.view.set(m.view);
				if (m.view?.timer) this.skew.set(Date.now() - m.view.timer.now);
				this.proposals.set((m.view?.proposals ?? []).filter((p: any) => !p.mine && p.answers?.[m.view.me.id] === 'pending'));
				if (m.view?.ending) this.ending.set(m.view.ending);
				break;
			case 'feed':
				this.feed.set(m.passages ?? []);
				break;
			case 'passages':
				this.feed.update((f) => [...f, ...m.passages].slice(-400));
				break;
			case 'observed':
				this.observed.update((f) => [...f, ...m.passages.map((p: Passage) => ({ ...p, observed: m.from }))].slice(-200));
				break;
			case 'gm_passages':
				this.gmPassages.update((f) => [...f, ...m.passages].slice(-400));
				break;
			case 'gm_state':
				this.gm.set(m.gm);
				break;
			case 'gm_feed':
				toast(m.item.data.message ?? `${m.item.kind}: ${m.item.data.name ?? ''}`, m.item.kind === 'warning' ? 'warn' : 'info');
				break;
			case 'gm_result': {
				const q = this.pending.get(m.op);
				const f = q?.shift();
				f?.(m.data);
				if (m.data && m.data.ok === false && m.data.error) toast(m.data.error, 'error');
				break;
			}
			case 'timer':
				this.view.update((v) => (v ? { ...v, timer: { deadline: m.deadline, kind: m.kind, now: m.now } } : v));
				break;
			case 'proposal':
				this.proposals.update((ps) => (ps.some((p) => p.id === m.proposal.id) ? ps : [...ps, m.proposal]));
				break;
			case 'toast':
				toast(m.text, m.kind === 'trait' ? 'trait' : m.kind === 'warn' ? 'warn' : 'info');
				break;
			case 'rejected':
				toast(m.reason, 'warn');
				break;
			case 'error':
				toast(m.error, 'error');
				break;
			case 'screen':
				this.screen.set(m.screen);
				break;
			case 'ending':
				this.ending.set(m.ending);
				break;
			case 'suggest':
				this.suggestion.set(m);
				break;
			case 'observe_end':
				this.observed.set([]);
				if (m.reason) toast(m.reason, 'info');
				break;
			case 'kicked':
				this.kicked.set(m.reason);
				break;
			case 'approved':
				toast('The GM let you in.', 'ok');
				break;
			case 'rewound':
				toast(`The story was rewound to beat ${m.beat}.`, 'warn');
				break;
		}
	}
}
